import { forwardRef } from "react";
import Shield from "lucide-react/dist/esm/icons/shield";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface RoleBadgeProps {
  role: "admin" | "moderator";
  size?: "sm" | "md" | "lg";
  showTooltip?: boolean;
  className?: string;
}

const roleConfig = {
  admin: {
    label: "Admin",
    tooltip: "Platform Administrator",
    icon: ShieldCheck,
    // Use destructive token for admin — clearly signals elevated/dangerous role
    colors: "bg-destructive/10 text-destructive border border-destructive/25",
    iconFill: "fill-destructive/30",
  },
  moderator: {
    label: "Mod",
    tooltip: "Platform Moderator",
    icon: Shield,
    // Use info token for moderator — informational authority, not alarming
    colors: "bg-info/10 text-info border border-info/25",
    iconFill: "fill-info/30",
  },
};

export const RoleBadge = forwardRef<HTMLSpanElement, RoleBadgeProps>(
  ({ role, size = "sm", showTooltip = true, className }, ref) => {
    const config = roleConfig[role];
    const Icon = config.icon;

    const sizeClasses = {
      sm: "h-3.5 w-3.5",
      md: "h-4 w-4",
      lg: "h-5 w-5",
    };

    const badgeSizes = {
      sm: "px-1.5 py-0.5 text-2xs gap-0.5",
      md: "px-2 py-0.5 text-xs gap-1",
      lg: "px-2.5 py-1 text-xs gap-1.5",
    };

    const badge = (
      <span
        className={cn(
          "inline-flex items-center rounded-full font-semibold uppercase tracking-wider",
          config.colors,
          badgeSizes[size],
          className
        )}
      >
        <Icon className={cn(sizeClasses[size], config.iconFill)} />
        {config.label}
      </span>
    );

    if (!showTooltip) {
      return <span ref={ref} className="inline-flex">{badge}</span>;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span ref={ref} className="inline-flex">{badge}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }
);

RoleBadge.displayName = "RoleBadge";
